import { Hint } from '../../components/ui/hint';
import { formatMoney } from '../../lib/format';
import { ShadowCoveragePanel } from './shadow-coverage';
import { useEffect, useRef, useState } from 'react';
import { api, errorLine } from '../../api/client';
import type { ConfigureShadow, ShadowUsage } from '../../api/types';
import { definitelyRejected, formatCredits } from '../../lib/credits';

export function ShadowUsageReport({ view }: { view: ShadowUsage }) {
  const t = view.totals;
  return (
    <>
      <div className="credit-balances shadow-balances">
        <div>
          <span>Tracked searches</span>
          <strong>{t.operations}</strong>
        </div>
        <div>
          <span>Hypothetical consumption</span>
          <strong>{formatCredits(t.completed_units)} credits</strong>
        </div>
        <div>
          <span>Unfinished searches</span>
          <strong>{formatCredits(t.pending_units)} potential credits</strong>
        </div>
      </div>
      <p className="field-note">
        Failed: {t.failed} · Cancelled: {t.cancelled} · Removed before completion:{' '}
        {t.removed_unfinished}
      </p>
      <div className="credit-history">
        <table className="table">
          <thead>
            <tr>
              <th>Quick Search</th>
              <th>Outcome</th>
              <th>Hypothetical credits</th>
              <th>Recorded provider cost</th>
            </tr>
          </thead>
          <tbody>
            {view.entries.map((e) => (
              <tr key={e.id}>
                <td>
                  {e.name}
                  <div>{new Date(e.created_at).toLocaleString()}</div>
                  <details className="row-detail">
                    <summary>Operation ID</summary>
                    {e.run_id}
                  </details>
                </td>
                <td>
                  {e.error_code === 'CANCELLED_BY_OFFICE' && e.state === 'failed'
                    ? 'Cancelled'
                    : e.state}
                  {e.source_removed ? ' (source removed)' : ''}
                  {e.error_code ? <div>{e.error_code}</div> : null}
                </td>
                <td>
                  {formatCredits(e.completed_units)}
                  {e.state !== 'complete' && e.state !== 'failed' && !e.source_removed
                    ? ` (${formatCredits(e.units)} pending)`
                    : ''}
                </td>
                <td>
                  {e.model_micro_usd === null && e.external_micro_usd === null
                    ? 'Not reported'
                    : formatMoney((e.model_micro_usd ?? 0) + (e.external_micro_usd ?? 0))}
                  {e.incomplete_usage ||
                  e.unrated_external ||
                  e.pending_model_projections ||
                  e.pending_pipeline_reports ? (
                    <div>
                      Incomplete usage: {e.incomplete_usage}; unpriced external calls:{' '}
                      {e.unrated_external}; pending model costs: {e.pending_model_projections};
                      pending pipeline reports: {e.pending_pipeline_reports}.
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!view.entries.length ? <p>No Quick Searches tracked yet.</p> : null}
    </>
  );
}
export function CreditShadowPanel({ orgId }: { orgId: string }) {
  const [view, setView] = useState<ShadowUsage | null>(null);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<ConfigureShadow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const lock = useRef(false);
  useEffect(() => {
    let live = true;
    api
      .shadowUsage(orgId)
      .then((v) => {
        if (live) setView(v);
      })
      .catch((e) => {
        if (live) setError(errorLine(e));
      });
    return () => {
      live = false;
    };
  }, [orgId]);
  async function action(work: () => Promise<void>) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (e) {
      setError(errorLine(e));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function configure() {
    if (!view) return;
    const body = pending ?? {
      command_key: crypto.randomUUID(),
      expected_version: view.current?.version ?? 0,
      enabled: !view.current?.enabled,
      reason: reason.trim(),
    };
    setPending(body);
    let saved;
    try {
      saved = await api.configureShadow(orgId, body);
    } catch (e) {
      if (definitelyRejected(e)) setPending(null);
      throw e;
    }
    setPending(null);
    setReason('');
    setView((v) => (v ? { ...v, current: saved } : v));
    setNotice(`Tracking change version ${saved.version} recorded. Current status is shown below.`);
    setView(await api.shadowUsage(orgId));
  }
  return (
    <section className="credit-card credit-shadow">
      <div className="credit-card-head">
        <h3>Automatic shadow accounting</h3>
        <span className="credit-badge">
          {view ? (view.current?.enabled ? 'Tracking on' : 'Tracking off') : 'Loading…'}
        </span>
      </div>
      <Hint label="About shadow tracking">
        1 credit = 1 Quick Search. There are no customer credit messages or deductions. Tracking
        captures new research, notes and imports; historical work is not backfilled. Raw Quick
        Search totals include completed searches with no confirmed match.
      </Hint>
      {!view && <p>Loading shadow tracking…</p>}
      <div className="field">
        <label className="label" htmlFor="shadow-setting-reason">
          Reason for tracking change
        </label>
        <input
          type="text"
          placeholder="For example: Observe pilot usage before setting prices"
          id="shadow-setting-reason"
          value={reason}
          maxLength={1000}
          disabled={busy || !!pending || !view}
          onChange={(e) => setReason(e.target.value)}
        />
        <Hint label="About the tracking reason">
          Internal audit note. Customers do not see this.
        </Hint>
      </div>
      <div className="form-actions">
        <button
          className="btn"
          type="button"
          disabled={busy || !view || (!pending && !reason.trim())}
          onClick={() => void action(configure)}
        >
          {pending
            ? 'Retry tracking change'
            : view?.current?.enabled
              ? 'Disable shadow tracking'
              : 'Enable shadow tracking'}
        </button>{' '}
        <button
          className="btn"
          type="button"
          disabled={busy}
          onClick={() => void action(async () => setView(await api.shadowUsage(orgId)))}
        >
          Refresh shadow usage
        </button>
      </div>
      {pending ? (
        <p>
          Unconfirmed request to {pending.enabled ? 'enable' : 'disable'} tracking. Retry to recover
          the same change.
        </p>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      <ShadowCoveragePanel orgId={orgId} revision={view?.current?.version} />
      <details>
        <summary>Quick Search detail</summary>
        {view ? <ShadowUsageReport view={view} /> : null}
        {view?.next_before ? (
          <button
            className="btn"
            type="button"
            disabled={busy}
            onClick={() =>
              void action(async () => {
                const next = await api.shadowUsage(orgId, view.next_before!);
                setView({ ...next, entries: [...view.entries, ...next.entries] });
              })
            }
          >
            Older tracked searches
          </button>
        ) : null}
      </details>
      <details>
        <summary>Recent tracking changes (up to 20)</summary>
        <ul>
          {view?.settings.map((s) => (
            <li key={s.id}>
              Version {s.version}: {s.enabled ? 'enabled' : 'disabled'} · {s.reason} ·{' '}
              {new Date(s.created_at).toLocaleString()} · {s.actor_user_id}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
